import { useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import DateNavigator, { PastDateBanner } from './DateNavigator';
import { CoachRow, won, shortWon } from './tabData';
import { CoachesResponse } from '../../types/opsBriefing';
import { useOpsContext } from './useOpsContext';
import {
    useOpsCoachesQuery,
    useSetOpsCoachHiddenMutation,
} from '../../services/useOpsBriefingService';
/*
 * `OpsComingSoonButton` 을 뺐습니다 — 이 화면의 유일한 사용처였던 카드 섹션의
 * 「면담 요청하기」·「칭찬 전하기」가 사라졌기 때문입니다.
 * 컴포넌트는 `OpsStates.tsx` 에 그대로 있고 지난 리포트가 계속 씁니다.
 */
import { OpsLoading, OpsError, OpsEmpty, OpsMockBadge } from './OpsStates';
import { isOpsBriefingMock } from '../../api/opsBriefing';
import {
    MetricComparison,
    METRICS,
    GRADE_META,
    SAMPLE_RULE_TEXT,
    eligibleLessonsAvg,
    NO_CENTER_AVG,
    analyzeAll,
    centerAvgOf,
    compareMetric,
    pickCoachesToAct,
    round1,
    judgementMembers,
    densityMembers,
    membersLabelOf,
    usesMonthlyScope,
    usesLessonMemberDensity,
    lessonDensityOf,
    lessonDensityAvgOf,
} from './coachAnalysis';
import CoachReportSheet from './CoachReportSheet';
import ValueBreakdownModal, { ValueTrigger } from './ValueBreakdownModal';
/*
 * 사분면 판정과 편차 막대는 **강사 데이터 드로어와 공유**한다.
 * 이 파일 안에 다시 두면 같은 강사가 화면마다 다른 유형·다른 막대로 보인다
 * (옛 CoachDetailDrawer 를 지운 이유가 그것이다).
 */
import { QUADRANTS, careScoreOf, CARE_AXIS_LABEL, quadrantOf } from './coachQuadrant';
import DeviationBar from './CoachDeviationBar';
import CoachDataDrawer from './CoachDataDrawer';

dayjs.locale('ko');

/**
 * 강사 현황.
 *
 * 이전 버전은 지표 아홉 개짜리 표였다. 숫자는 다 있었지만
 * "이 강사가 뭘 잘하고 뭘 못하는가"는 원장이 직접 계산해야 했다. 아무도 안 한다.
 *
 * 그래서 순서를 바꾼다.
 *   ② 성과 매트릭스 — 팀 전체 배치를 크게
 *   ③ 강사별 강점·약점 — 분석 결과를 먼저, 원본 표는 접어서
 *
 * ①「오늘 이야기 나눌 강사」 카드 섹션이 맨 위에 있었는데 2026-08-27 대표 결정으로
 * 뺐다 — ②③ 이 같은 정보를 이미 주고 있었다(본문 ① 주석에 근거를 적어 두었다).
 * 번호는 ②③ 그대로 둔다. 다시 매기면 화면·주석·테스트에 흩어진 「② 성과 매트릭스」
 * 라는 이름이 한꺼번에 어긋난다.
 */

const BORDER = '#E3EAF3';

/**
 * 🔴 빈 칸이 **왜 비었는지**를 서버가 준 분모로 설명한다 (2026-08-31 QA14).
 *
 * ─────────────────────────────────────────────────────────────
 * 「표본부족」은 틀린 말이었다
 * ─────────────────────────────────────────────────────────────
 * 이 표는 값이 `null` 이면 무조건 「표본부족」이라 적고 있었다. 그런데 여기엔
 * **문턱 판정이 없다** — `checkSample`(활성 5명·수업 10회)은 등급·사분면에만
 * 걸리고 이 셀에는 안 붙는다. 즉 「표본이 얇다」고 말할 근거가 없었다.
 *
 * 서버 계약이 말하는 `null` 의 뜻은 다르다:
 *   `closingRate` — "기회를 배정받지 않았으면 null" (types/opsBriefing.ts)
 * 즉 **분모가 0** 이라 비율을 낼 수 없다는 뜻이다. 「표본이 얇다」와
 * 「대상이 아예 없다」는 원장이 취할 행동이 정반대다 —
 * 앞은 기다리는 것이고, 뒤는 신규 기회를 배정하는 것이다.
 *
 * 다행히 **분모는 이미 오고 있다**(`CoachRowDto.basis`). 화면이 안 썼을 뿐이다.
 * 그래서 추측하지 않고 그 값으로 이유를 적는다.
 */
const BASIS_OF: Partial<
    Record<SortKey, { key: 'closing' | 'conversion' | 'journal' | 'noshow'; zero: string; why: string }>
> = {
    closingRate: {
        key: 'closing',
        zero: '기회 없음',
        why: '이 달에 배정받은 신규 기회가 0건이라 클로징률을 낼 수 없습니다',
    },
    conversionPct: {
        key: 'conversion',
        zero: '대상 없음',
        why: '이 달에 정상 종료한 담당 회원이 0명이라 재등록 성공률을 낼 수 없습니다',
    },
    journalPct: {
        key: 'journal',
        zero: '대상 없음',
        why: '이 달에 일지를 쓸 수업이 0건이라 작성률을 낼 수 없습니다',
    },
    noshowPct: {
        key: 'noshow',
        zero: '대상 없음',
        why: '이 달에 예정된 수업이 0건이라 노쇼율을 낼 수 없습니다',
    },
};

/** 빈 칸에 적을 말과 그 이유 — 모르면 「모른다」고 한다 */
export const emptyCellReason = (
    col: SortKey,
    basis: CoachRow['basis']
): { text: string; title: string } => {
    const meta = BASIS_OF[col];
    if (!meta) return { text: '집계 전', title: '이 지표는 아직 집계되지 않았습니다' };

    /*
     * 🔴 **`basis` 키 부재와 `basis[key] === null` 은 다른 사실이다.**
     *
     *   키 부재   분모를 아예 안 보내는 세대(구서버·목)다 → 이유를 **모른다.**
     *             「기회 없음」이라 단정하면 실제로는 집계가 안 된 것뿐인데
     *             원장이 배정을 의심한다.
     *   `null`    서버가 **「분모 0」이라고 말한 것**이다 — 운영 서버는
     *             `eligible`(분모 > 0)이 아니면 basis 를 null 로 내린다
     *             (`opsCoaches.basisByCoach` · `allPassConversionOf`).
     *             이걸 「서버가 분모를 안 보냈다」로 적으면, 「이 달 대상이 0건」인
     *             흔한 경우에 대고 서버 탓을 하게 된다.
     */
    if (basis == null) {
        return { text: '집계 전', title: '이 서버는 분모를 보내지 않아 이유를 알 수 없습니다' };
    }

    const b = basis[meta.key];
    /*
     * ⚠️ 분모 0 은 **집계가 실패한 달에도 같은 모양**으로 온다(원천 조회가 빈 값으로
     *    떨어지면 분모가 0 이 된다). 이 응답에는 둘을 가를 신호가 없으므로
     *    「대상이 없다」고 단정하지 않고 두 가능성을 함께 적는다.
     */
    if (b == null || b.denominator === 0) {
        return {
            text: meta.zero,
            title: `${meta.why} (집계가 실패한 달에도 같은 모양으로 오니, 전원이 비어 있으면 다시 불러와 주세요)`,
        };
    }
    /* 분모는 있는데 값이 없다 — 집계가 덜 됐다는 뜻이다 */
    return {
        text: '집계 전',
        title: `대상 ${b.denominator}건은 있는데 값이 아직 집계되지 않았습니다`,
    };
};

/* ─────────────── 원본 표 정렬 ─────────────── */

type SortKey =
    | 'name' | 'members' | 'lessons' | 'lessonsPerMember' | 'conversionPct' | 'closingRate' | 'journalPct'
    | 'journalReadPct' | 'sameDayCancel' | 'noshowPct' | 'responseHours' | 'portfolioValue';

type ColumnDef = {
    key: SortKey; label: string; align?: 'right'; unit?: string;
    /** 센터 평균. 아직 못 재는 지표는 null 이고 비교 색을 칠하지 않는다 */
    lowerIsBetter?: boolean; avg?: number | null;
};

/** 표의 평균 비교 색도 센터 평균에서 나와야 합니다 */
/**
 * 값이 한 명도 없는 컬럼은 표에서 뺀다.
 *
 * 일지열람·당일취소·응답 세 컬럼은 서버가 늘 null 을 보내서(집계 경로 없음)
 * 전 행이 '—' 로 찼습니다. 목 데이터에만 값이 있어 화면에서는 멀쩡해 보였습니다.
 * 지우지 않고 각주로 이름을 밝힙니다 — 그냥 사라지면 원장이
 * "원래 없는 지표"인지 "우리 센터만 안 나오는지" 구분할 수 없습니다.
 */
const dropEmptyColumns = (cols: ColumnDef[], rows: CoachRow[]) => {
    const hasValue = (k: SortKey) =>
        rows.some((r) => (r as unknown as Record<string, unknown>)[k] != null);
    /*
     * 🔴 **서버가 분모로 이유를 설명해 주는 지표는 값이 전부 비어도 남긴다** (`BASIS_OF`).
     *
     * 이 컬럼들의 빈 값은 「아직 안 재는 지표」가 아니라 「이 달 대상이 0건」이다.
     * 컬럼을 통째로 빼면 그 사실이 아래 각주의 「아직 집계하지 않는 지표」로 뭉개져서,
     * 원장은 라포가 이 지표를 아예 안 재는 줄 안다. 작은 센터는 전 강사가
     * 대상 0건인 달이 흔하다. 칸마다 `emptyCellReason` 이 이유를 적는다.
     *
     * ⚠️ 일지열람·당일취소·응답은 `BASIS_OF` 에 없다 — 집계 경로 자체가 없어서
     *    예전처럼 그대로 빠진다. 이 예외가 그 셋을 되살리지 않는다.
     */
    const explainsItself = (k: SortKey) => BASIS_OF[k] != null;
    const kept = cols.filter(
        (c) => c.key === 'name' || c.key === 'lessonsPerMember' || explainsItself(c.key) || hasValue(c.key)
    );
    return { kept, dropped: cols.filter((c) => !kept.includes(c)).map((c) => c.label) };
};

/**
 * 표 머리글·색 기준으로 쓸 **회원당 수업 수 평균**.
 *
 * 🔴 행 값(`lessonDensityOf`)과 **같은 분모**에서 나온 평균만 쓴다.
 *    행은 「수업 회원」으로 나누는데 평균이 「월간 담당」이면, 그 비교로 칠한
 *    초록·빨강이 전부 거짓말이 된다. 잠수회원이 많은 센터일수록 두 분모가
 *    크게 벌어져서 멀쩡한 강사가 빨간색이 된다.
 *    (PDF·리포트는 `lessonDensityAvgOf` 로 이미 짝을 맞추고 있었는데 표만 갈렸다)
 *
 * ⚠️ 표 머리글의 평균은 하나뿐이라 **강사마다 세대가 다르면 쓸 수 없다.**
 *    그때는 null 을 준다 — 「쌍이 안 맞으면 비교하지 않는다」는 이 파일의 원칙이고,
 *    틀린 기준으로 칠하는 것보다 안 칠하는 편이 낫다.
 */
const tableDensityAvg = (
    avg: CoachesResponse['centerAvg'],
    rows: CoachRow[]
): number | null => {
    if (rows.length === 0) return null;
    const first = lessonDensityAvgOf(avg, rows[0]);
    const sameForAll = rows.every((r) => lessonDensityAvgOf(avg, r) === first);
    return sameForAll ? (first ?? null) : null;
};

const buildColumns = (avg: CoachesResponse['centerAvg'], rows: CoachRow[]): ColumnDef[] => [
    { key: 'name', label: '강사' },
    { key: 'members', label: '담당', align: 'right', unit: '명' },
    { key: 'lessons', label: '수업', align: 'right', unit: '회' },
    {
        key: 'lessonsPerMember',
        label: '회원당 수업',
        align: 'right',
        unit: '회',
        /*
         * 🔴 분모가 짝이 맞는 평균만 쓴다 — 계산기(`lessonDensityAvgOf`)가 고른다.
         *    여기에 `lessonsPerMonthlyMember ?? lessonsPerActiveMember` 식을 다시
         *    적으면, 행은 「수업 회원」으로 나누는데 평균은 「월간 담당」이 되어
         *    같은 표 안에서 두 분모를 비교하게 된다.
         */
        avg: tableDensityAvg(avg, rows),
    },
    /* 🔴 이름은 계산기(`coachAnalysis.METRICS`)의 라벨과 같은 말이어야 한다 — 같은 표 안에서 갈리면 안 된다 */
    { key: 'conversionPct', label: '재등록 성공률', align: 'right', unit: '%', avg: avg.conversionPct },
    { key: 'closingRate', label: '신규 클로징률', align: 'right', unit: '%', avg: avg.closingRate },
    { key: 'journalPct', label: '일지작성', align: 'right', unit: '%', avg: avg.journalPct },
    { key: 'journalReadPct', label: '일지열람', align: 'right', unit: '%', avg: avg.journalReadPct },
    { key: 'sameDayCancel', label: '당일취소', align: 'right', unit: '건', lowerIsBetter: true, avg: avg.sameDayCancel },
    { key: 'noshowPct', label: '노쇼율', align: 'right', unit: '%', lowerIsBetter: true, avg: avg.noshowPct },
    { key: 'responseHours', label: '응답', align: 'right', unit: 'h', lowerIsBetter: true, avg: avg.responseHours },
    /* 🔴 「담당 잔여가치」에서 바꿨다 (2026-08-31 정책 M-D4·M8). 이 값은 서버가 주는
       Σ(잔여 회차 × 회당 단가) — 이미 받았지만 아직 수업하지 않은 금액이다.
       「가치」는 앞으로 들어올 돈처럼 읽혀서, 회원 현황의 「지켜야 할 매출」과
       같은 돈인데 다른 이름으로 서 있었다. */
    { key: 'portfolioValue', label: '담당 미사용 선수금', align: 'right' },
];

/* ─────────────── 편차 막대 ─────────────── */

/**
 * 지표 비교 패널 — 칩을 누르면 열린다.
 *
 * "전환율 21.4% (평균 31.7%)"까지는 칩에 담기지만,
 * 1위가 58.3%라는 걸 봐야 격차가 실감난다. 면담에서 근거로 쓰려면 이 폭이 필요하다.
 */
const CONF_LABEL: Record<string, { text: string; color: string; bg: string; desc: string }> = {
    VERIFIED: {
        text: '검증됨',
        color: '#00795F',
        bg: '#E3F1EC',
        desc: '실제 이탈 데이터로 예측력이 확인된 지표입니다',
    },
    WEAK: {
        text: '약한 신호',
        color: '#B45309',
        bg: '#FEF3C7',
        desc: '예측력은 있으나 크지 않아 판정에 절반만 반영합니다',
    },
    REFERENCE: {
        text: '참고',
        color: '#7C8794',
        bg: '#F1F4F9',
        desc: '회원 사정이 섞이거나 검증되지 않아 평가 근거로 쓰지 않습니다',
    },
};

const MetricCompare = ({ cmp, onClose }: { cmp: MetricComparison; onClose: () => void }) => {
    const max = Math.max(...cmp.points.map((p) => p.value)) * 1.2 || 1;
    const conf = CONF_LABEL[cmp.metric.confidence];
    /*
     * 🔴 톤은 **계산기가 정한다** (`cmp.tone`). 여기서 `rank` 로 다시 재면,
     *    화면과 인쇄물이 같은 강사를 다르게 부를 수 있다.
     */
    const weak = cmp.tone === 'WEAK';
    /** 센터 안에서의 자리를 못 정한 경우 — 우열 문구도 색도 만들지 않는다 */
    const neutral = cmp.tone === 'NEUTRAL';

    return (
        <div
            className="rounded-[11px]"
            style={{ marginTop: 11, border: `1px solid ${BORDER}`, background: '#FCFDFE', padding: '16px 18px' }}
        >
            <div className="flex items-center" style={{ gap: 8 }}>
                <span style={{ fontSize: 15.5, fontWeight: 800, color: '#16202E' }}>{cmp.metric.label}</span>
                <span
                    className="rounded-[5px]"
                    title={conf.desc}
                    style={{ background: conf.bg, color: conf.color, fontSize: 11, fontWeight: 800, padding: '3px 8px', cursor: 'help' }}
                >
                    {conf.text}
                </span>
                {cmp.rank != null && cmp.rankOf != null ? (
                    <span
                        className="rounded-[5px]"
                        style={{
                            background: weak ? '#FFF0F0' : '#EAF7F2',
                            color: weak ? '#C22D38' : '#00795F',
                            fontSize: 12, fontWeight: 800, padding: '3px 9px',
                        }}
                    >
                        센터 {cmp.rankOf}명 중 {cmp.rank}위
                    </span>
                ) : null}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="비교 닫기"
                    className="ml-auto rounded-[7px]"
                    style={{ width: 28, height: 28, border: `1px solid ${BORDER}`, background: '#FFFFFF', color: '#93A0AE', fontSize: 15, cursor: 'pointer' }}
                >
                    ×
                </button>
            </div>

            <span style={{ fontSize: 13.5, color: '#5B6B7F', display: 'block', marginTop: 8, lineHeight: '20px' }}>
                {cmp.metric.why}
            </span>

            {/* 비교 막대 */}
            <div className="flex flex-col" style={{ gap: 8, marginTop: 14 }}>
                {cmp.points.map((pt) => (
                    <div key={pt.label} className="flex items-center" style={{ gap: 11 }}>
                        <span
                            style={{
                                fontSize: 12.5, width: 132, flex: 'none', textAlign: 'right',
                                color: pt.isMine ? '#16202E' : '#7C8794',
                                fontWeight: pt.isMine ? 700 : 400,
                            }}
                        >
                            {pt.label}
                        </span>
                        <div style={{ flex: 1, position: 'relative', height: 24 }}>
                            <div
                                style={{
                                    width: `${(pt.value / max) * 100}%`,
                                    height: 24,
                                    borderRadius: 5,
                                    minWidth: 4,
                                    background: pt.isMine
                                        ? neutral
                                            ? '#9AA5B2'
                                            : weak
                                              ? '#FF5A64'
                                              : '#00A793'
                                        : pt.good
                                          ? '#8FD9C8'
                                          : '#C4CCD8',
                                }}
                            />
                            <span
                                style={{
                                    position: 'absolute',
                                    left: `calc(${(pt.value / max) * 100}% + 9px)`,
                                    top: 2,
                                    fontSize: 15,
                                    fontWeight: pt.isMine ? 800 : 600,
                                    color: pt.isMine ? '#16202E' : '#7C8794',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {pt.value}
                                <span style={{ fontSize: 11.5, fontWeight: 600 }}>{cmp.metric.unit}</span>
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/*
              🔴 순위를 못 정했으면 **우열 문구를 내지 않는다**.
                 「이렇게 활용하세요」는 잘한다는 말이고, 「이렇게 해보세요」는
                 못한다는 말이다. 대신 왜 판정하지 않았는지를 적는다 —
                 빈 칸은 고장으로 읽힌다.
            */}
            <div
                className="rounded-[9px]"
                style={{
                    marginTop: 13,
                    background: neutral ? '#F5F7FA' : weak ? '#FFF7F7' : '#F0F7F4',
                    borderLeft: `3px solid ${neutral ? '#C4CCD8' : weak ? '#FF5A64' : '#00A793'}`,
                    padding: '11px 13px',
                }}
            >
                <span style={{ fontSize: 12, color: '#93A0AE' }}>
                    {neutral ? '아직 판단하지 않았습니다' : weak ? '이렇게 해보세요' : '이렇게 활용하세요'}
                </span>
                <p style={{ fontSize: 14, color: '#2C3A4B', marginTop: 3, lineHeight: '20px' }}>
                    {neutral
                        ? '센터 안에서의 자리를 정하지 못해 좋다 나쁘다를 말할 수 없습니다. 값은 참고로만 보세요.'
                        : weak
                          ? cmp.metric.action
                          : cmp.metric.leverage}
                </p>
            </div>
        </div>
    );
};

/* ─────────────── 본문 ─────────────── */

/** 토스트 한 줄 — 되돌릴 수 있는 동작이면 그 자리에 되돌리기를 함께 단다 */
type CoachToast = { text: string; undo?: { label: string; run: () => void } };

const OpsBriefingCoachesPage = () => {
    const [sortKey, setSortKey] = useState<SortKey>('conversionPct');
    const [asc, setAsc] = useState(false);
    const [selected, setSelected] = useState<CoachRow | null>(null);
    const [hoverId, setHoverId] = useState<string | null>(null);
    const [quadFilter, setQuadFilter] = useState<string | null>(null);
    // 표는 기본으로 펼쳐 둔다. 숫자를 바로 보고 싶은 원장이 매번 누르게 하면 안 된다.
    const [openTable, setOpenTable] = useState(true);
    const [openMetric, setOpenMetric] = useState<{ coachId: string; key: string } | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [date, setDate] = useState<Dayjs>(() => dayjs().startOf('day'));
    /** 가린 강사를 다시 꺼내 보고 있나 */
    const [showHidden, setShowHidden] = useState(false);
    /** 이름을 눌러 연 강사 데이터 드로어 (오늘의 브리핑과 같은 드로어) */
    const [openData, setOpenData] = useState<string | null>(null);
    /** 헤더 금액을 눌러 연 「왜 이 금액인가」 팝업 (2026-09-01 QA31) */
    const [showPortfolio, setShowPortfolio] = useState(false);
    const [toast, setToast] = useState<CoachToast | null>(null);
    /*
     * ⚠️ 이 화면의 조회 단위는 **달**입니다 (아래 useOpsCoachesQuery 는 'YYYY-MM').
     * 예전에는 여기서 일 단위로 오늘인지 따져서, 같은 달 안에서 날짜만 바꿔도
     * "3일 전 기준입니다" 배너가 떴습니다. 숫자는 하나도 안 바뀌는데 화면만
     * 바뀐 척을 하니 "날짜가 먹통"으로 보였습니다. 판정을 조회 단위에 맞춥니다.
     */
    const isThisMonth = date.isSame(dayjs(), 'month');

    const ctx = useOpsContext();
    const { data, isLoading, isError, refetch } = useOpsCoachesQuery(
        ctx,
        date.format('YYYY-MM'),
        ctx.ready
    );

    const allCoaches = useMemo(() => data?.coaches ?? [], [data]);
    const allClosingRows = useMemo(() => data?.closing ?? [], [data]);

    /**
     * 서버가 강사 가리기를 아는가 — **키의 존재**로 판정한다.
     *
     * `hidden` 이 없으면 이 서버에 가리기 엔드포인트가 아예 없다는 뜻이라,
     * 버튼을 그리면 눌러서 404 를 받게 됩니다. 그래서 값이 아니라 키를 봅니다
     * (회원 현황의 `caps.showHide` 와 같은 판정).
     *
     * ⚠️ 낙관 반영은 `hidden` 키를 지우지 않고 true/false 로만 바꾸므로,
     *    가리는 순간 이 판정이 뒤집혀 UI 가 사라지는 일은 없습니다.
     */
    const canHide = useMemo(
        () =>
            allCoaches.some((c) => c.hidden !== undefined) ||
            /*
             * 클로징 표도 본다. 퇴사 강사는 `coaches[]` 에서 빠지고 `closing[]` 에만
             * 남는 경우가 있어서, 강사 목록이 비어 있어도 가릴 대상이 있을 수 있다.
             */
            allClosingRows.some((c) => c.hidden !== undefined),
        [allCoaches, allClosingRows]
    );

    /**
     * 가려 둔 강사는 목록·사분면·하단 표·순위 모집단에서 **전부** 빠진다.
     *
     * 아래 계산(analyses·visible·rows·plotted·avgLessons·totalPortfolio …)은 모두
     * `coaches` 를 보므로, 여기서 한 번 거르면 화면 전체가 같은 기준을 씁니다.
     *
     * ⚠️ **센터 평균(`centerAvg`)과 센터 클로징(`centerClosing`)은 그대로입니다.**
     *    서버가 계산해 내려주는 값이라 프론트가 뺄 수 없고, 서버도 일부러 빼지
     *    않습니다 (rappo `opsCoachHide.model.ts` — 분모를 몰래 줄이면 남은 강사들의
     *    상대 평가가 조용히 달라진다). 그래서 아래 접이식 안내에 그 사실을 적습니다.
     */
    const coaches = useMemo(() => allCoaches.filter((c) => c.hidden !== true), [allCoaches]);

    /**
     * 가려 둔 강사 — **되돌릴 목록**.
     *
     * ⚠️ `coaches[]` 만 보면 안 된다. `coaches[]` 는 지금 연결된 강사만 담고
     *    `closing[]`(그 달 신규기회)에는 **퇴사한 강사가 남는다.** 그리고 이 기능의
     *    주 대상이 바로 퇴사 강사다 — 즉 가린 강사가 `closing[]` 에만 있는 경우가
     *    실제로 생긴다. 그때 목록을 `coaches[]` 로만 만들면 그 강사는 화면에서
     *    사라진 채 **되돌릴 길이 없어진다.**
     *
     * 그래서 두 배열의 합집합으로 만든다. 이름 말고는 아는 것이 다를 수 있어
     * (`closing` 행에는 담당·수업 수가 없다) 각자 아는 만큼만 적는다.
     */
    const hiddenCoaches = useMemo(() => {
        const rows = allCoaches.filter((c) => c.hidden === true);
        const known = new Set(rows.map((c) => c.coachId));
        const closingOnly = allClosingRows
            .filter((c) => c.hidden === true && !known.has(c.coachId))
            .map((c) => ({ coachId: c.coachId, name: c.name, row: null }));
        return [
            ...rows.map((c) => ({ coachId: c.coachId, name: c.name, row: c as CoachRow })),
            ...closingOnly,
        ];
    }, [allCoaches, allClosingRows]);

    /*
     * 클로징 표(하단 지표 표)도 같이 거른다.
     * 강사 행의 `hidden` 과 클로징 행의 `hidden` 을 **둘 다** 본다 —
     * 서버가 한쪽에만 실어 보내도 목록과 표가 갈리지 않게 하려는 것이다.
     */
    const closingRows = useMemo(() => {
        const hiddenIds = new Set(
            allCoaches.filter((c) => c.hidden === true).map((c) => c.coachId)
        );
        return allClosingRows.filter((c) => c.hidden !== true && !hiddenIds.has(c.coachId));
    }, [allClosingRows, allCoaches]);
    /*
     * 비교 기준선.
     * 서버가 centerAvg 를 안 줬으면 '전부 null' 로 떨어진다 — 목 상수로 메우지 않는다.
     * 예전에는 여기서 COACH_CENTER_AVG 를 임시로 썼고, 그러면 응답이 늦는 동안
     * 실제 강사가 목 데이터 평균(전환율 31.7%·일지 48%)과 비교돼
     * 집중 관리·점검 필요 배지를 달았다. 기준을 모르면 판정하지 않는 것이 맞다.
     * 전부 null 이면 사분면은 놓이지 않고(quadrantOf → null) 등급은 NA 가 된다.
     */
    const avg = data?.centerAvg ?? NO_CENTER_AVG;
    const centerClosing = data?.centerClosing;
    const convMid = avg.conversionPct;
    /* 세로축 기준선 — 축을 바꿨으니 기준도 같은 지표의 센터 평균이어야 한다 */
    const careMid = avg.journalPct;
    /* 두 축 모두 값이 있어야 사분면에 놓을 수 있다 */
    const plotted = coaches.filter((c) => quadrantOf(c, convMid, careMid) != null);
    const unplotted = coaches.length - plotted.length;
    const { kept: COLUMNS, dropped: droppedColumns } = useMemo(
        () => dropEmptyColumns(buildColumns(avg, coaches), coaches),
        [avg, coaches]
    );

    /*
     * 토스트를 띄우던 두 버튼(면담 요청·칭찬 전하기)은 **버튼째 사라졌다** —
     * 그 버튼이 붙어 있던 카드 섹션을 뺐기 때문이다(아래 ① 주석).
     * 원래 서버로 아무것도 안 보내면서 "보냈습니다"만 말하던 자리였고,
     * 그 뒤로는 잠금 버튼이었다. 실제로 보내게 되면 그때 되살릴 자리는
     * 이 화면이 아니라 강사 상세(데이터 드로어) 쪽이 맞다.
     */

    /*
     * 센터 평균을 반드시 넘긴다.
     * 안 넘기면 목 데이터에서 나온 상수(COACH_CENTER_AVG)와 비교하게 되고,
     * 이제 막 문을 연 센터가 남의 기준으로 "심각한 약점" 판정을 받는다.
     */
    const analyses = useMemo(() => analyzeAll(coaches, avg), [coaches, avg]);
    /** 강사별 신규기회 실적 — 배정 못 받은 강사는 없다 */
    const closingOf = (coachId: string) => closingRows.find((c) => c.coachId === coachId) ?? null;
    /*
     * 센터 수업량 평균.
     * 소수 첫째 자리까지만 남긴다 — 나누면 172.66666666666666 같은 값이 나오고,
     * 이 값은 비교 배지에 "평균 172.66666666666666회"로 그대로 찍힌다.
     * 판정에 쓰는 편차는 이 정도 반올림으로 달라지지 않는다.
     */
    /*
     * ⚠️ analyzeAll 이 등급을 매길 때 쓰는 것과 **같은 함수**를 쓴다.
     * 예전에는 여기서 따로 나눴는데, 유보 강사를 한쪽에서만 빼면
     * 등급의 근거와 화면에 보이는 '평균 N회'가 다른 기준선이 된다.
     */
    const avgLessons = useMemo(() => eligibleLessonsAvg(coaches), [coaches]);
    const byId = useMemo(
        () => new Map(analyses.map((a) => [a.coach.coachId, a])),
        [analyses]
    );
    /*
     * 머리말의 「오늘 챙길 N명」과 「챙길 강사 몫 N원」이 이 값을 씁니다.
     * 카드 섹션은 걷어냈지만(아래 ① 주석) 이 두 숫자는 남았습니다 —
     * 숫자 하나로 요약하는 것과 카드 넉 장으로 늘어놓는 것은 다른 일입니다.
     */
    const todo = useMemo(() => pickCoachesToAct(analyses), [analyses]);
    /*
     * 🔴 `pickCoachToLearnFrom`(이달의 기준 강사)은 이 화면에서 더 쓰지 않습니다.
     *    카드 섹션이 유일한 사용처였습니다. 함수 자체는 `coachAnalysis.ts` 에
     *    그대로 두었습니다 — 테스트가 잠그고 있는 분석 원시함수라,
     *    화면 하나가 안 쓴다고 지울 값이 아닙니다.
     */

    const visible = useMemo(() => {
        const base = quadFilter ? analyses.filter((a) => quadrantOf(a.coach, convMid, careMid) === quadFilter) : analyses;
        /*
         * 문제 있는 강사가 위로 — 명단이 곧 처리 순서가 된다.
         * 판정이 안 된 강사(NA)와 표본이 모자란 강사(INSUFFICIENT)는 맨 아래.
         * 좋아서가 아니라 아직 말할 수 없어서다.
         */
        const order = {
            CRITICAL: 0,
            WATCH: 1,
            GOOD: 2,
            EXCELLENT: 3,
            INSUFFICIENT: 4,
            NA: 5,
        } as const;
        return [...base].sort((a, b) => {
            if (order[a.grade] !== order[b.grade]) return order[a.grade] - order[b.grade];
            return b.coach.portfolioValue - a.coach.portfolioValue;
        });
    }, [analyses, quadFilter, convMid, careMid]);

    const rows = useMemo(() => {
        const base = quadFilter ? coaches.filter((c) => quadrantOf(c, convMid, careMid) === quadFilter) : coaches;
        return [...base].sort((a, b) => {
            if (sortKey === 'name') return a.name.localeCompare(b.name, 'ko') * (asc ? 1 : -1);
            const val = (c: CoachRow) =>
                sortKey === 'lessonsPerMember'
                    ? /* 정렬도 화면과 같은 분모를 봐야 한다 — 갈리면 정렬이 거짓말이 된다 */
                      (lessonDensityOf(c) ?? -1)
                    : ((c[sortKey as keyof CoachRow] as number | null) ?? -1);
            return (val(a) - val(b)) * (asc ? 1 : -1);
        });
    }, [coaches, sortKey, asc, quadFilter, convMid, careMid]);

    const totalPortfolio = coaches.reduce((s, c) => s + c.portfolioValue, 0);
    const atRiskValue = todo.reduce((s, a) => s + a.coach.portfolioValue, 0);

    /* ── 강사 가리기 ──────────────────────────────────────────
     *
     * ⚠️ 오늘의 브리핑의 **항목 가리기와 다른 물건**입니다. 회원 가리기와 같은 축이라
     *    (센터, 강사) 한 쌍이고 **다시 보기 전까지 영구**입니다. 그래서 요청에 달이
     *    없습니다(useSetOpsCoachHiddenMutation).
     *
     * ⚠️ **평가 유보(INSUFFICIENT) 강사에게만** 버튼을 답니다. 대표 요청의 쓰임새가
     *    "퇴사한 강사 정리"이고, 그런 강사는 이번 달 표본이 없어 유보로 떨어집니다.
     *    평가가 살아 있는 강사까지 가릴 수 있게 하면, 성적이 나쁜 강사를 화면에서
     *    치워 놓고 잊는 도구가 됩니다.
     *
     * 낙관 반영과 실패 롤백은 훅이 맡습니다. 여기서는 **성공했을 때만** 성공 문구를
     * 띄웁니다 — 서버에 아직 엔드포인트가 없으면 404 가 오고, "가렸습니다" 라고 해
     * 놓고 새로고침에 되돌아오는 것이 가장 나쁩니다.
     * ────────────────────────────────────────────────────── */

    const setHidden = useSetOpsCoachHiddenMutation(ctx, date.format('YYYY-MM'));
    /** 지금 가리기·다시 보기 요청이 나가 있는 강사 (요청 중에는 모든 버튼을 잠근다) */
    const [pendingHideId, setPendingHideId] = useState<string | null>(null);

    const showToast = (next: CoachToast, ms = 2400) => {
        setToast(next);
        window.setTimeout(() => setToast((cur) => (cur === next ? null : cur)), ms);
    };
    const fire = (msg: string) => showToast({ text: msg });

    const failMessage = (error: unknown) => {
        const msg = (error as { response?: { data?: { message?: string } } } | null)?.response?.data
            ?.message;
        return msg || '처리하지 못했습니다. 잠시 후 다시 시도해 주세요';
    };

    /** 가린 강사를 다시 목록으로 (서버에서도 해제된다) */
    /*
     * ⚠️ 인자를 `CoachRow` 로 좁히지 않는다. 클로징 표에만 남은 강사(퇴사자)는
     *    `coaches[]` 에 없어서 CoachRow 를 만들 수 없는데, 그런 강사야말로
     *    되돌릴 수 있어야 한다. 되돌리는 데 필요한 것은 id 와 이름뿐이다.
     */
    const handleUnhide = (c: { coachId: string; name: string }) => {
        if (setHidden.isLoading) return;
        setPendingHideId(c.coachId);
        setHidden.mutate(
            { coachId: c.coachId, hidden: false },
            {
                onSuccess: () => fire(`${c.name} 강사를 다시 표시합니다`),
                onError: (e) => fire(failMessage(e)),
                onSettled: () => setPendingHideId(null),
            }
        );
    };

    const handleHide = (c: CoachRow) => {
        if (setHidden.isLoading) return;
        /* 리포트를 열어 둔 채 가리면 목록에 없는 강사의 리포트만 남는다 */
        if (selected?.coachId === c.coachId) setSelected(null);
        if (expanded === c.coachId) setExpanded(null);
        setPendingHideId(c.coachId);
        setHidden.mutate(
            { coachId: c.coachId, hidden: true },
            {
                onSuccess: () =>
                    showToast(
                        {
                            text: `${c.name} 강사를 가렸습니다 · 다시 보기 전까지 강사 현황에서 숨겨집니다`,
                            undo: { label: '되돌리기', run: () => handleUnhide(c) },
                        },
                        /* 되돌리기를 누를 시간을 준다 — 회원 가리기와 같은 6초 */
                        6000
                    ),
                onError: (e) => fire(failMessage(e)),
                onSettled: () => setPendingHideId(null),
            }
        );
    };

    const isOpen = (coachId: string, key: string) =>
        openMetric?.coachId === coachId && openMetric?.key === key;

    const toggleMetric = (coachId: string, key: string) =>
        setOpenMetric((prev) =>
            prev?.coachId === coachId && prev?.key === key ? null : { coachId, key }
        );

    const toggleSort = (k: SortKey) => {
        if (k === sortKey) setAsc((v) => !v);
        else {
            setSortKey(k);
            setAsc(k === 'name');
        }
    };

    /* ── 상태 분기 ─────────────────────────────────────────── */

    if (!ctx.ready) {
        return (
            <OpsError
                title="센터 정보를 확인할 수 없습니다"
                detail="로그인 정보가 만료되었을 수 있습니다"
            />
        );
    }

    if (isLoading) {
        return (
            <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
                <DateNavigator value={date} onChange={setDate} label="강사 현황" unit="month" />
                <OpsLoading variant="page" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
                <DateNavigator value={date} onChange={setDate} label="강사 현황" unit="month" />
                <OpsError onRetry={() => refetch()} />
            </div>
        );
    }

    /*
     * 강사가 정말 없는 경우.
     *
     * ⚠️ `coaches`(가린 강사를 뺀 목록)가 아니라 `allCoaches` 를 봅니다.
     *    강사를 **전부** 가려 둔 상태에서 여기로 빠지면 "등록된 강사가 없습니다"가
     *    떠서 원장님은 데이터가 사라진 줄 알고, 무엇보다 아래의 「가린 강사 N명 보기」가
     *    통째로 사라져 **되돌릴 길이 막힙니다.** 회원 현황이 같은 자리에서 겪은 일입니다.
     */
    if (allCoaches.length === 0) {
        return (
            <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
                <DateNavigator value={date} onChange={setDate} label="강사 현황" unit="month" />
                <OpsEmpty
                    icon="🧑‍🏫"
                    title="등록된 강사가 없습니다"
                    body="강사를 등록하고 수업이 쌓이면 강점과 개선 포인트를 자동으로 분석해 드립니다."
                    hint="재등록 성공률은 수강권이 정상 종료된 회원이 생겨야 계산됩니다. 보통 한두 달 걸립니다."
                />
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
            <DateNavigator value={date} onChange={setDate} label="강사 현황" unit="month" />
            {isOpsBriefingMock() && <OpsMockBadge />}
            {!isThisMonth && (
                <PastDateBanner
                    date={date}
                    unit="month"
                    onBackToToday={() => setDate(dayjs().startOf('day'))}
                />
            )}
            {/*
              🔴 **이번 달은 아직 안 끝났다** (2026-09-01 QA21).

              매달 1일 아침에 이 화면을 열면 숫자가 전부 0에 가깝다. 한 달치 실적이
              사라진 것처럼 보이지만 실제로는 **오늘 하루치**다. 그 사실을 안 적으면
              원장님은 「초기화됐다」로 읽고, 그 다음에는 지난달을 볼 생각을 못 한다.

              ⚠️ 「중간 집계」라고 부르는 것은 리포트 지면과 **같은 관례**다
                 (`CoachPdfReport` 의 periodLabel). 한 화면에서만 다른 말을 쓰면
                 원장이 두 숫자를 다른 것으로 읽는다.

              지난달로 가는 길을 **여기서 준다.** 내비게이션의 ◀ 를 눌러야 한다는
              것을 모르는 채로 「데이터가 없다」고 결론 내리는 것이 QA21 의 요지였다.
            */}
            {isThisMonth && (
                <div
                    className="flex w-full items-center rounded-[12px]"
                    style={{
                        background: '#FFF8E8',
                        border: '1px solid #F0DFB4',
                        padding: '13px 18px',
                        gap: 10,
                    }}
                >
                    <span style={{ fontSize: 16 }}>⏳</span>
                    <span style={{ fontSize: 14.5, color: '#8A5A00', fontWeight: 700, flex: 'none' }}>
                        {date.format('YYYY년 M월')}은 진행 중입니다
                    </span>
                    <span style={{ fontSize: 13.5, color: '#A07B2E' }}>
                        아래 숫자는 {date.format('M월 1일')}부터 오늘까지의{' '}
                        <b style={{ color: '#8A5A00' }}>중간 집계</b>라 월말 실적이 아닙니다.
                        달이 바뀐 직후에는 특히 작게 보입니다.
                    </span>
                    <button
                        type="button"
                        onClick={() => setDate(date.subtract(1, 'month').startOf('month'))}
                        className="ml-auto rounded-[8px]"
                        style={{
                            height: 34,
                            padding: '0 14px',
                            background: '#FFFFFF',
                            border: '1px solid #E3CE9A',
                            color: '#8A5A00',
                            fontSize: 13.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                            flex: 'none',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {date.subtract(1, 'month').format('M월')} 완결 실적 보기
                    </button>
                </div>
            )}

            {/* 헤더 */}
            <div
                className="w-full rounded-[16px]"
                style={{
                    background: 'linear-gradient(135deg, #1A305D 0%, #24406F 55%, #2B5183 100%)',
                    padding: '24px 30px',
                    boxShadow: '0 10px 30px rgba(26,48,93,0.18)',
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        {/* '이번 달'로 박아 두면 7월을 보고 있어도 이번 달이라고 우긴다 */}
                        <span style={{ fontSize: 13, color: '#9DB4D8' }}>
                            강사 성과 · {isThisMonth ? '이번 달' : date.format('YYYY년 M월')}
                        </span>
                        <span style={{ fontSize: 26, fontWeight: 800, color: '#FFFFFF', marginTop: 7, letterSpacing: '-0.03em' }}>
                            강사 {coaches.length}명
                            {todo.length > 0 && (
                                <span style={{ fontSize: 17, color: '#FFB0B6', marginLeft: 10, fontWeight: 700 }}>
                                    오늘 챙길 {todo.length}명
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="flex items-center">
                        {[
                            {
                                label: '👥 강사들이 들고 있는 잔여 수강권',
                                v: `${won(totalPortfolio)}원`,
                                sub: `챙길 강사 몫 ${shortWon(atRiskValue)}원`,
                                tone: '#FFFFFF',
                                /* 누르면 강사별로 얼마씩인지 편다 (2026-09-01 QA31) */
                                open: () => setShowPortfolio(true),
                            },
                            {
                                // 신규 클로징은 기존 회원 유지와 다른 매출 축이라 함께 세운다
                                label: '🎯 신규기회 클로징',
                                v: centerClosing ? `${centerClosing.closed}명 결제` : '—',
                                /*
                                 * closingRate 는 number | null 이다(types/opsBriefing.ts).
                                 * 템플릿 리터럴이라 JSX 의 null 무시가 안 먹어서,
                                 * 기회를 배정받은 적이 없는 달에는 남색 헤더에
                                 * "12명 만나 null%" 가 그대로 찍혔다.
                                 */
                                /*
                                 * ⚠️ centerClosing 은 **서버 집계**라 가린 강사가 계속 들어 있다.
                                 *    아래 강사 줄의 '신규 클로징' 배지는 가린 강사를 뺀 값이라
                                 *    두 숫자가 안 맞을 수 있다 — 그 이유를 여기서 밝힌다.
                                 */
                                sub: !centerClosing
                                    ? '집계 준비 중'
                                    : `${
                                          centerClosing.closingRate == null
                                              ? `${centerClosing.leadMembers}명 만남 · 클로징률 아직 없음`
                                              : `${centerClosing.leadMembers}명 만나 ${centerClosing.closingRate}%`
                                      }${hiddenCoaches.length > 0 ? ' · 가린 강사 포함' : ''}`,
                                tone: '#8FE3CD',
                            },
                        ].map((x, i) => (
                            <div
                                key={x.label}
                                className="flex flex-col"
                                style={{
                                    padding: '0 26px',
                                    borderLeft: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.14)',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <span style={{ fontSize: 12.5, color: '#9DB4D8' }}>{x.label}</span>
                                {'open' in x && x.open ? (
                                    <ValueTrigger
                                        label={`${x.label} 산출 근거 보기`}
                                        onClick={x.open}
                                        style={{
                                            fontSize: 25,
                                            fontWeight: 800,
                                            color: x.tone,
                                            letterSpacing: '-0.03em',
                                            marginTop: 4,
                                        }}
                                    >
                                        {x.v}
                                    </ValueTrigger>
                                ) : (
                                    <span
                                        style={{ fontSize: 25, fontWeight: 800, color: x.tone, letterSpacing: '-0.03em', marginTop: 4 }}
                                    >
                                        {x.v}
                                    </span>
                                )}
                                <span style={{ fontSize: 12, color: '#8FA5C4', marginTop: 2 }}>{x.sub}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/*
              ① 「오늘 이야기 나눌 강사」 카드 섹션을 뺐습니다 (2026-08-27 대표 결정).

              집중 관리·이달의 기준 카드 서너 장이 이 자리 맨 위에 서 있었습니다.
              그런데 같은 이야기를 아래 **② 성과 매트릭스**(팀 전체 배치)와
              **③ 강사별 강점·개선 포인트**(강사마다 무엇이 좋고 무엇이 문제인가)가
              이미 하고 있었습니다. 같은 강사를 한 화면에서 세 번 설명하면
              원장님은 어느 것이 오늘의 결론인지 고르지 못합니다.

              ⚠️ **선별 로직(`pickCoachesToAct`)은 지우지 않았습니다.**
                 이 화면 머리말의 「오늘 챙길 N명」·「챙길 강사 몫 N원」이 같은 값을 쓰고,
                 **오늘의 브리핑의 강사 인사이트 줄**도 같은 함수를 씁니다
                 (`briefingComposition.pickCoachInsight` — "두 화면이 다른 기준으로
                 「오늘 챙길 강사」를 고르면 원장님은 어느 쪽을 믿어야 할지 알 수 없다").
                 여기서 지우면 그 두 곳이 조용히 달라집니다.

              카드에만 있던 「면담 요청하기」·「칭찬 전하기」 버튼도 함께 사라집니다.
              둘 다 서버 API 가 없어 눌러도 아무 일이 없던 잠금 버튼이라
              (`OpsComingSoonButton`) 실제로 잃는 동작은 없습니다.
              지난 리포트의 「감사 메시지 보내기」는 다른 자리라 그대로 있습니다.
            */}

            {/* ② 성과 매트릭스 */}
            <div className="rounded-[16px]" style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '22px 24px' }}>
                <div className="flex items-baseline justify-between">
                    <div className="flex items-baseline" style={{ gap: 10 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#16202E' }}>성과 매트릭스</span>
                        <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                            점 크기 = 담당 회원 수 · 누르면 상세 리포트
                        </span>
                    </div>
                    {quadFilter && (
                        <button
                            type="button"
                            onClick={() => setQuadFilter(null)}
                            className="rounded-[7px]"
                            style={{ height: 30, padding: '0 12px', background: '#F1F4F9', border: 'none', color: '#5B6B7F', fontSize: 13, cursor: 'pointer' }}
                        >
                            필터 해제 ×
                        </button>
                    )}
                </div>

                <div className="flex" style={{ gap: 20, marginTop: 16 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex">
                            <div className="flex flex-none flex-col justify-between" style={{ width: 40, height: 440, paddingBottom: 26 }}>
                                <span style={{ fontSize: 11.5, color: '#7C8794', fontWeight: 600 }}>성실 ↑</span>
                                <span style={{ fontSize: 11, color: '#A7B0BC', writingMode: 'vertical-rl', margin: '0 auto' }}>
                                    일지 · 응답 · 약속
                                </span>
                                <span style={{ fontSize: 11.5, color: '#7C8794', fontWeight: 600 }}>성실 ↓</span>
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    className="relative"
                                    style={{ height: 440, border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden', background: '#FCFDFE' }}
                                >
                                    {/*
                                      사분면 라벨은 배경 워터마크로 둔다.
                                      점 위에 얹으면 성실도가 중간인 강사와 반드시 겹친다.
                                      정확한 인원과 설명은 오른쪽 목록이 맡는다.
                                    */}
                                    {[
                                        { q: 'CARE', top: 0, left: 0, ly: { top: 12 }, lx: { left: 14 } },
                                        { q: 'STAR', top: 0, left: '50%', ly: { top: 12 }, lx: { right: 14 } },
                                        { q: 'RISK', top: '50%', left: 0, ly: { bottom: 12 }, lx: { left: 14 } },
                                        { q: 'SALES', top: '50%', left: '50%', ly: { bottom: 12 }, lx: { right: 14 } },
                                    ].map((z) => {
                                        const meta = QUADRANTS.find((x) => x.key === z.q)!;
                                        return (
                                            <div
                                                key={z.q}
                                                className="absolute"
                                                style={{ top: z.top, left: z.left, width: '50%', height: '50%', background: meta.bg, zIndex: 0 }}
                                            >
                                                <span
                                                    className="absolute"
                                                    style={{
                                                        ...z.ly, ...z.lx,
                                                        fontSize: 13,
                                                        fontWeight: 800,
                                                        color: meta.color,
                                                        opacity: 0.42,
                                                        letterSpacing: '0.02em',
                                                        pointerEvents: 'none',
                                                    }}
                                                >
                                                    {meta.label}
                                                </span>
                                            </div>
                                        );
                                    })}

                                    <div className="absolute" style={{ left: '50%', top: 0, bottom: 0, width: 1, background: '#C9D4E2' }} />
                                    <div className="absolute" style={{ top: '50%', left: 0, right: 0, height: 1, background: '#C9D4E2' }} />

                                    {plotted.map((c) => {
                                        /*
                                         * 두 축 모두 값이 있는 강사만 찍는다.
                                         * 못 잰 값을 가운데(50)로 두면 신규 강사가 전부
                                         * 한 점에 겹치고, 원장은 그걸 '평균'으로 읽는다.
                                         */
                                        const conv = c.conversionPct as number;
                                        const care = careScoreOf(c) as number;
                                        const x =
                                            convMid == null || convMid <= 0
                                                ? 50
                                                : Math.min(Math.max((conv / (convMid * 2)) * 100, 9), 91);
                                        const y =
                                            careMid == null || careMid <= 0
                                                ? 50
                                                : 100 - Math.min(Math.max((care / (careMid * 2)) * 100, 9), 91);
                                        const q = quadrantOf(c, convMid, careMid);
                                        const meta = QUADRANTS.find((x2) => x2.key === q)!;
                                        const dim = !!quadFilter && q !== quadFilter;
                                        const r = 22 + Math.min(c.members, 26);
                                        const hot = hoverId === c.coachId;
                                        return (
                                            <button
                                                key={c.coachId}
                                                type="button"
                                                onClick={() => setSelected(c)}
                                                onMouseEnter={() => setHoverId(c.coachId)}
                                                onMouseLeave={() => setHoverId(null)}
                                                className="absolute flex items-center justify-center rounded-full"
                                                style={{
                                                    left: `${x}%`, top: `${y}%`, width: r, height: r,
                                                    transform: `translate(-50%, -50%) scale(${hot ? 1.12 : 1})`,
                                                    background: meta.color,
                                                    border: '3px solid #FFFFFF',
                                                    boxShadow: hot ? `0 0 0 6px ${meta.color}33` : '0 2px 6px rgba(0,0,0,0.20)',
                                                    opacity: dim ? 0.18 : 1,
                                                    cursor: 'pointer', padding: 0,
                                                    zIndex: hot ? 6 : 3,
                                                    transition: 'transform .14s, box-shadow .14s',
                                                    color: '#FFFFFF', fontSize: 12, fontWeight: 800,
                                                }}
                                                aria-label={`${c.name} 강사 상세`}
                                            >
                                                {c.members}
                                                <span
                                                    style={{
                                                        position: 'absolute', top: r + 4,
                                                        fontSize: 12.5, fontWeight: 700, color: '#3A4757', whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {c.name}
                                                </span>
                                            </button>
                                        );
                                    })}

                                    {hoverId && (
                                        <div
                                            className="absolute rounded-[10px]"
                                            style={{ right: 12, bottom: 12, background: '#16202E', color: '#FFFFFF', padding: '12px 15px', zIndex: 10, maxWidth: 270 }}
                                        >
                                            {(() => {
                                                const a = byId.get(hoverId)!;
                                                return (
                                                    <>
                                                        <span style={{ fontSize: 14.5, fontWeight: 800 }}>
                                                            {a.coach.name} 강사 · {GRADE_META[a.grade].label}
                                                        </span>
                                                        <span style={{ fontSize: 12.5, color: '#B7C7E0', display: 'block', marginTop: 5, lineHeight: '18px' }}>
                                                            재등록 성공률 {a.coach.conversionPct ?? '—'}% ·{' '}
                                                            {CARE_AXIS_LABEL} {careScoreOf(a.coach) ?? '—'}%
                                                            <br />
                                                            담당 {a.coach.members}명 · 미사용 선수금 {shortWon(a.coach.portfolioValue)}원
                                                            {a.topIssue && (
                                                                <>
                                                                    <br />
                                                                    <span style={{ color: '#FFB0B6' }}>
                                                                        약점 {a.topIssue.metric.label}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </span>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {/*
                                      🔴 **빈 매트릭스는 고장처럼 보인다** (2026-09-01 QA22).

                                      월초에는 점이 하나도 안 놓인다 — 판정에 필요한 표본
                                      (활성 5명·수업 10회)이 아직 안 쌓였기 때문이다. 그런데
                                      화면은 축과 사분면 라벨만 남은 빈 상자라, 원장님은
                                      「데이터가 안 나온다」로 읽는다.

                                      비어 있는 이유와 **언제 채워지는지**를 그 자리에 적는다.
                                      그리고 지난달로 가는 길을 준다 — 1일 아침에 볼 것이
                                      없는 게 아니라 **지난달에는 있다.**

                                      ⚠️ 「강사가 없다」와 「표본이 안 쌓였다」를 가른다.
                                         전자는 강사를 등록해야 하고, 후자는 기다리면 된다.
                                         두 문장을 뭉개면 원장이 할 수 없는 일을 시키게 된다.
                                    */}
                                    {plotted.length === 0 && (
                                        <div
                                            className="absolute inset-0 flex flex-col items-center justify-center"
                                            style={{ padding: '0 32px', textAlign: 'center', gap: 8 }}
                                        >
                                            <span style={{ fontSize: 30 }}>📊</span>
                                            <span style={{ fontSize: 15, fontWeight: 700, color: '#5B6B7F' }}>
                                                아직 매트릭스에 놓을 만큼 쌓이지 않았습니다
                                            </span>
                                            {/*
                                              ⚠️ 「강사가 한 명도 없다」는 여기서 말하지 않는다 —
                                                 그 경우는 화면 전체가 빈 상태로 빠져(allCoaches 0)
                                                 이 매트릭스 자체가 안 그려진다. 여기까지 왔다는 것은
                                                 **강사는 있는데 표본이 얇다**는 뜻이고, 원장이 할 일도
                                                 다르다(등록이 아니라 기다리는 것).
                                            */}
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    color: '#93A0AE',
                                                    lineHeight: '20px',
                                                    maxWidth: 420,
                                                }}
                                            >
                                                {isThisMonth ? '이번 달' : date.format('M월')} 수업 기록이 쌓이면
                                                강사님들이 여기에 표시됩니다. {SAMPLE_RULE_TEXT}.
                                            </span>
                                            {/*
                                              지난달 보기 유도 — 이번 달을 보고 있을 때만.
                                              지난달을 보는데 또 「지난달 보기」를 권하면 안 된다.
                                            */}
                                            {isThisMonth && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setDate(date.subtract(1, 'month').startOf('month'))
                                                    }
                                                    className="rounded-[8px]"
                                                    style={{
                                                        marginTop: 4,
                                                        height: 34,
                                                        padding: '0 14px',
                                                        background: '#FFFFFF',
                                                        border: `1px solid ${BORDER}`,
                                                        color: '#2C4C8C',
                                                        fontSize: 13.5,
                                                        fontWeight: 700,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {date.subtract(1, 'month').format('M월')} 실적 보기 →
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-between" style={{ padding: '9px 4px 0' }}>
                                    <span style={{ fontSize: 11.5, color: '#7C8794', fontWeight: 600 }}>← 재등록 성공률 낮음</span>
                                    <span
                                        style={{ fontSize: 11.5, color: '#93A0AE' }}
                                        title={
                                            hiddenCoaches.length > 0
                                                ? '기준선은 서버가 센터 전체로 계산한 평균이라 가린 강사도 포함합니다. 점(강사)만 뺐습니다.'
                                                : undefined
                                        }
                                    >
                                        센터 평균 {convMid == null ? '—' : round1(convMid)}%
                                        {hiddenCoaches.length > 0 && ' (가린 강사 포함)'}
                                    </span>
                                    <span style={{ fontSize: 11.5, color: '#7C8794', fontWeight: 600 }}>재등록 성공률 높음 →</span>
                                </div>
                                {/* 못 실은 강사를 밝힌다. 안 밝히면 원장은 그 강사가 없다고 생각한다 */}
                                {unplotted > 0 && (
                                    <div style={{ padding: '4px 4px 0' }}>
                                        <span style={{ fontSize: 11, color: '#93A0AE' }} title={SAMPLE_RULE_TEXT}>
                                            표본이 모자라 {unplotted}명은 여기에 놓지 않았습니다. 아래 표에는 그대로
                                            있습니다.
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 사분면 설명 — 각 칸에서 뭘 해야 하는지 */}
                    <div className="flex flex-none flex-col" style={{ width: 268, gap: 9 }}>
                        {QUADRANTS.map((q) => {
                            const list = coaches.filter((c) => quadrantOf(c, convMid, careMid) === q.key);
                            const active = quadFilter === q.key;
                            return (
                                <button
                                    key={q.key}
                                    type="button"
                                    onClick={() => setQuadFilter(active ? null : q.key)}
                                    className="flex flex-col rounded-[11px]"
                                    style={{
                                        border: `1px solid ${active ? q.color : BORDER}`,
                                        background: active ? q.bg : '#FFFFFF',
                                        padding: '12px 14px', textAlign: 'left', cursor: 'pointer',
                                    }}
                                >
                                    <div className="flex items-center" style={{ gap: 7 }}>
                                        <span style={{ width: 9, height: 9, borderRadius: 5, background: q.color, flex: 'none' }} />
                                        <span style={{ fontSize: 14.5, fontWeight: 700, color: '#16202E' }}>{q.label}</span>
                                        <span style={{ fontSize: 13.5, fontWeight: 800, color: q.color, marginLeft: 'auto' }}>
                                            {list.length}명
                                        </span>
                                    </div>
                                    {list.length > 0 && (
                                        <span style={{ fontSize: 12.5, color: q.color, marginTop: 5, fontWeight: 600 }}>
                                            {list.map((c) => c.name).join(', ')}
                                        </span>
                                    )}
                                    <span style={{ fontSize: 12, color: '#7C8794', marginTop: 6, lineHeight: '17px' }}>
                                        {q.howTo}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ③ 강사별 강점·약점 */}
            <div>
                <div className="mb-[10px] flex items-baseline" style={{ gap: 10 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#16202E' }}>강사별 강점과 개선 포인트</span>
                    <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                        센터 평균과 비교해 자동으로 뽑았습니다 · 먼저 봐야 할 강사부터
                    </span>
                </div>

                <div className="flex flex-col" style={{ gap: 10 }}>
                    {visible.map((a) => {
                        const g = GRADE_META[a.grade];
                        const open = expanded === a.coach.coachId;
                        return (
                            <div
                                key={a.coach.coachId}
                                className="w-full rounded-[14px]"
                                style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, borderLeft: `4px solid ${g.color}`, overflow: 'hidden' }}
                            >
                                <div style={{ padding: '16px 20px' }}>
                                    <div className="flex items-center" style={{ gap: 9 }}>
                                        {/*
                                          이름을 누르면 **강사 데이터 드로어**가 열린다.
                                          오늘의 브리핑 강사 카드와 같은 드로어이고, 같은
                                          분석 결과(analyses)를 넘긴다 — 두 화면이 다른 값을
                                          먹으면 같은 강사가 다른 등급으로 보인다.
                                          우측 '리포트' 버튼(PDF)은 그대로 둔다.
                                        */}
                                        <button
                                            type="button"
                                            onClick={() => setOpenData(a.coach.coachId)}
                                            aria-label={`${a.coach.name} 강사 데이터 보기`}
                                            title={`${a.coach.name} 강사의 지표를 자세히 봅니다`}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                padding: 0,
                                                fontSize: 17.5,
                                                fontWeight: 800,
                                                color: '#16202E',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                            }}
                                        >
                                            {a.coach.name}
                                        </button>
                                        <span
                                            className="rounded-[5px]"
                                            title={g.desc}
                                            style={{ background: g.bg, color: g.color, fontSize: 11.5, fontWeight: 800, padding: '3px 8px', cursor: 'help' }}
                                        >
                                            {g.label}
                                        </span>
                                        {/*
                                          판정 지표가 0개인 강사는 예전에 '안정' 배지를 달았다.
                                          배지만 회색으로 바꾸면 "조용히 잘하고 있나 보다"로 읽히므로,
                                          왜 등급이 없는지를 그 자리에서 문장으로 밝힌다.
                                        */}
                                        {a.grade === 'NA' && (
                                            <span style={{ fontSize: 13, color: '#7C8794' }}>
                                                아직 판단할 수 없습니다 · 비교할 지표가 모이지 않았습니다
                                            </span>
                                        )}
                                        {/*
                                          유보는 왜 유보인지를 그 자리에서 밝힌다.
                                          '평가 유보'만 있으면 원장님은 시스템이 고장 났다고 읽는다.
                                          무엇이 모자란지(회원인지 수업인지) 알아야 다음 행동이 정해진다.
                                        */}
                                        {a.grade === 'INSUFFICIENT' && (
                                            <span
                                                style={{ fontSize: 13, color: '#7C8794', cursor: 'help' }}
                                                title={SAMPLE_RULE_TEXT}
                                            >
                                                표본 부족 ({a.sample.reason}) · 좋다 나쁘다를 아직 말할 수 없습니다
                                            </span>
                                        )}
                                        {/*
                                          🆕 「수업 회원」을 함께 적는다.
                                             「월간 담당 20명인데 수업 회원 3명」은 밀도가 낮은 게 아니라
                                             잠수가 많은 것이다. 두 숫자가 같이 보여야 그 구분이 된다.
                                        */}
                                        <span style={{ fontSize: 13.5, color: '#7C8794' }}>
                                            담당 {a.coach.members}명 ({membersLabelOf(a.coach)}{' '}
                                            {judgementMembers(a.coach)}명
                                            {usesLessonMemberDensity(a.coach) &&
                                                ` · 수업 회원 ${densityMembers(a.coach)}명`}
                                            ) · 수업 {a.coach.lessons}회
                                        </span>
                                        {/* 총량보다 밀도가 중요하다 — 담당이 적으면 총 수업도 적은 게 정상이다 */}
                                        {lessonDensityOf(a.coach) != null && (
                                            <span
                                                className="rounded-[5px]"
                                                title={
                                                    `총 수업을 ${membersLabelOf(a.coach, 'density')} 수로 나눈 값입니다. ` +
                                                    '담당이 적은 강사가 부당하게 낮게 잡히지 않도록 이 값으로 판단합니다.' +
                                                    (usesLessonMemberDensity(a.coach)
                                                        ? '\n\n분모는 그 달에 실제로 수업한 회원 수입니다 — 분자를 만든 바로 그 수업들의 회원이라 기준이 같습니다.' +
                                                          '\n수강권만 있고 한 번도 안 온 회원은 빼고 셉니다(회원 현황의 이탈 위험에서 따로 봅니다).' +
                                                          '\n노쇼도 수업 1회로 세므로 노쇼만 있는 회원은 분모에 들어갑니다.'
                                                        : usesMonthlyScope(a.coach)
                                                          ? '\n\n분모는 그 달에 실제로 담당한 회원 수입니다 — 분자(그 달 수업 수)와 기준이 같습니다.'
                                                          : '\n\n서버가 월 단위 담당 수를 주지 않아 오늘 기준 활성 회원 수로 나눴습니다.')
                                                }
                                                style={{
                                                    background: '#F1F5FC',
                                                    color: '#2C4C8C',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    padding: '3px 8px',
                                                    cursor: 'help',
                                                }}
                                            >
                                                회원당 {lessonDensityOf(a.coach)}회
                                                <span style={{ fontWeight: 500, color: '#7C8794' }}>
                                                    {' '}
                                                    {/* 기준이 없으면 숫자를 만들지 않는다 — null 을 반올림하면 NaN 이 찍힌다 */}
                                                    / 평균{' '}
                                                    {(() => {
                                                        /* 🔴 분모가 짝이 맞는 평균만 쓴다 */
                                                        const d = lessonDensityAvgOf(avg, a.coach);
                                                        return d == null ? '—' : round1(d);
                                                    })()}
                                                    회
                                                </span>
                                            </span>
                                        )}
                                        {/*
                                          지금 손이 필요한 두 가지.

                                          예전에는 이 둘이 우측 슬라이드 패널 안에만 있었고,
                                          그 패널을 완성본 리포트로 갈아치우면서 화면에서 사라졌습니다.
                                          리포트는 월간 면담 문서라 "지금 미응답 3건"이 들어갈 자리가
                                          아닙니다 — 목록 행이 맞는 자리입니다. 여기서 보고 바로 처리합니다.

                                          0 이면 배지를 만들지 않습니다. 잘하고 있는 강사 줄에
                                          '0건' 배지가 붙으면 그것도 챙길 거리처럼 보입니다.
                                        */}
                                        {(a.coach.pendingRequests ?? 0) > 0 && (
                                            <span
                                                className="rounded-[5px]"
                                                title="강사가 아직 응답하지 않은 예약 요청입니다. 회원은 답을 기다리는 중입니다."
                                                style={{
                                                    background: '#FFF0F0',
                                                    color: '#C22D38',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    padding: '3px 8px',
                                                    cursor: 'help',
                                                }}
                                            >
                                                미응답 예약 {a.coach.pendingRequests}건
                                            </span>
                                        )}
                                        {(a.coach.appUnlinked ?? 0) > 0 && (
                                            <span
                                                className="rounded-[5px]"
                                                title="앱을 연동하지 않은 담당 회원입니다. 일지도 알림도 닿지 않아 관계가 끊기기 쉽습니다."
                                                style={{
                                                    /* 4명 이상이면 눈에 걸리게 — 낡은 패널이 쓰던 기준을 그대로 옮겼다 */
                                                    background:
                                                        (a.coach.appUnlinked ?? 0) >= 4 ? '#FEF3C7' : '#F1F4F9',
                                                    color: (a.coach.appUnlinked ?? 0) >= 4 ? '#B45309' : '#5B6B7F',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    padding: '3px 8px',
                                                    cursor: 'help',
                                                }}
                                            >
                                                앱 미연동 {a.coach.appUnlinked}명
                                            </span>
                                        )}
                                        <span className="ml-auto flex items-center" style={{ gap: 12 }}>
                                            {(() => {
                                                const cl = closingOf(a.coach.coachId);
                                                if (!cl) return null;
                                                return (
                                                    <span
                                                        style={{ fontSize: 13.5, color: '#7C8794' }}
                                                        title={`유형별 ${cl.byType.map((t) => `${t.name} ${t.closed}/${t.leadMembers}`).join(' · ')}`}
                                                    >
                                                        신규 클로징{' '}
                                                        <b style={{ color: cl.closed > 0 ? '#00795F' : '#A7B0BC' }}>
                                                            {cl.closed}/{cl.leadMembers}명
                                                        </b>
                                                    </span>
                                                );
                                            })()}
                                            <span style={{ fontSize: 13.5, color: '#7C8794' }}>
                                                미사용 선수금{' '}
                                                <b style={{ color: '#16202E' }}>{won(a.coach.portfolioValue)}원</b>
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setSelected(a.coach)}
                                                className="rounded-[8px]"
                                                style={{ height: 34, padding: '0 13px', background: '#FFFFFF', border: `1px solid ${BORDER}`, color: '#425063', fontSize: 13.5, cursor: 'pointer' }}
                                            >
                                                리포트
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setExpanded(open ? null : a.coach.coachId)}
                                                className="rounded-[8px]"
                                                style={{ height: 34, padding: '0 13px', background: open ? '#EEF3FB' : '#FFFFFF', border: `1px solid ${open ? '#B9CBE6' : BORDER}`, color: '#425063', fontSize: 13.5, cursor: 'pointer' }}
                                            >
                                                {open ? '지표 접기 ▲' : '지표 펼치기 ▼'}
                                            </button>
                                            {/*
                                              가리기 — **평가 유보 강사에게만.**
                                              쓰임새가 퇴사 강사 정리라서다. 평가가 살아 있는
                                              강사까지 가릴 수 있으면 성적이 나쁜 강사를 화면에서
                                              치워 놓고 잊는 도구가 된다.
                                              (`canHide` 가 false 면 서버가 이 기능을 모르는
                                               구버전이라 버튼 자체를 안 그린다 — 누르면 404 다)
                                            */}
                                            {canHide && a.grade === 'INSUFFICIENT' && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleHide(a.coach)}
                                                    disabled={pendingHideId != null}
                                                    aria-disabled={pendingHideId != null}
                                                    aria-label={`${a.coach.name} 강사 가리기`}
                                                    title={`${a.coach.name} 강사를 가립니다 · 다시 보기 전까지 강사 현황에서 숨겨집니다`}
                                                    className="rounded-[8px]"
                                                    style={{
                                                        height: 34,
                                                        padding: '0 13px',
                                                        background: '#FFFFFF',
                                                        border: `1px solid ${BORDER}`,
                                                        color: pendingHideId != null ? '#B4BDC8' : '#7C8794',
                                                        fontSize: 13.5,
                                                        cursor: pendingHideId != null ? 'progress' : 'pointer',
                                                    }}
                                                >
                                                    가리기
                                                </button>
                                            )}
                                        </span>
                                    </div>

                                    {/*
                                      유보 강사에게는 강점·약점을 그리지 않는다.
                                      배지는 '평가 유보'인데 바로 아래에 "▲ 잘하고 있는 것"이
                                      붙으면 화면이 스스로 모순된다. 원장님은 아래쪽 초록 상자를
                                      읽고 그 강사를 잘한다고 판단한다 — 표본 1명에서 나온 값인데도.
                                      대신 무엇이 쌓여야 평가가 시작되는지를 적는다.
                                    */}
                                    {a.grade === 'INSUFFICIENT' ? (
                                        <div
                                            className="rounded-[10px]"
                                            style={{
                                                marginTop: 13,
                                                background: '#F7F9FC',
                                                border: `1px solid ${BORDER}`,
                                                padding: '11px 13px',
                                            }}
                                        >
                                            <span style={{ fontSize: 13, color: '#5B6B7F' }}>
                                                표본이 모자라 강점·개선점을 가르지 않았습니다 (
                                                {a.sample.reason}). {SAMPLE_RULE_TEXT}.
                                            </span>
                                            <span
                                                style={{ fontSize: 12.5, color: '#93A0AE', display: 'block', marginTop: 4 }}
                                            >
                                                아래 &lsquo;지표 펼치기&rsquo;에서 지금까지 기록된 숫자는 그대로 보실 수 있습니다.
                                            </span>
                                        </div>
                                    ) : (
                                    <div className="flex" style={{ gap: 10, marginTop: 13 }}>
                                        <div
                                            className="flex flex-1 flex-col rounded-[10px]"
                                            style={{ background: '#F4FCF9', border: '1px solid #C6E5DA', padding: '11px 13px', minWidth: 0 }}
                                        >
                                            <span style={{ fontSize: 12, fontWeight: 800, color: '#00795F' }}>
                                                ▲ 잘하고 있는 것
                                            </span>
                                            {a.strengths.length === 0 ? (
                                                <span style={{ fontSize: 13, color: '#8FA79F', marginTop: 5 }}>
                                                    아직 평균을 크게 넘어선 지표가 없습니다
                                                </span>
                                            ) : (
                                                <div className="flex flex-wrap" style={{ gap: 5, marginTop: 6 }}>
                                                    {a.strengths.slice(0, 3).map((d) => (
                                                        <button
                                                            key={d.metric.key}
                                                            type="button"
                                                            onClick={() => toggleMetric(a.coach.coachId, d.metric.key)}
                                                            title="눌러서 센터 전체와 비교"
                                                            className="rounded-[6px]"
                                                            style={{
                                                                background: isOpen(a.coach.coachId, d.metric.key) ? '#E3F1EC' : '#FFFFFF',
                                                                border: '1px solid #B7DFD2', color: '#00795F',
                                                                fontSize: 12.5, fontWeight: 700, padding: '5px 10px', cursor: 'pointer',
                                                            }}
                                                        >
                                                            {d.metric.label} {d.value}
                                                            {d.metric.unit}{' '}
                                                            <span style={{ fontWeight: 500, fontSize: 11.5 }}>
                                                                (평균 {d.avg}{d.metric.unit})
                                                            </span>
                                                            {!d.judgeable && (
                                                                <span style={{ fontWeight: 700, fontSize: 10.5, color: '#93A0AE', marginLeft: 4 }}>
                                                                    참고
                                                                </span>
                                                            )}
                                                            <span style={{ fontSize: 11, color: '#8FBFB0', marginLeft: 4 }}>›</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            className="flex flex-1 flex-col rounded-[10px]"
                                            style={{ background: '#FFF7F7', border: '1px solid #F8C9CC', padding: '11px 13px', minWidth: 0 }}
                                        >
                                            <span style={{ fontSize: 12, fontWeight: 800, color: '#C22D38' }}>
                                                ▼ 개선하면 좋은 것
                                            </span>
                                            {a.weaknesses.length === 0 ? (
                                                <span style={{ fontSize: 13, color: '#C09FA2', marginTop: 5 }}>
                                                    지금 손볼 지표가 없습니다
                                                </span>
                                            ) : (
                                                <div className="flex flex-wrap" style={{ gap: 5, marginTop: 6 }}>
                                                    {a.weaknesses.slice(0, 3).map((d) => (
                                                        <button
                                                            key={d.metric.key}
                                                            type="button"
                                                            onClick={() => toggleMetric(a.coach.coachId, d.metric.key)}
                                                            title="눌러서 센터 전체와 비교"
                                                            className="rounded-[6px]"
                                                            style={{
                                                                background: isOpen(a.coach.coachId, d.metric.key)
                                                                    ? (d.judgeable ? '#FFE8E9' : '#F1F4F9')
                                                                    : '#FFFFFF',
                                                                border: `1px solid ${d.judgeable ? '#FBD0D3' : '#E3EAF3'}`,
                                                                color: d.judgeable ? '#C22D38' : '#7C8794',
                                                                fontSize: 12.5, fontWeight: 700, padding: '5px 10px', cursor: 'pointer',
                                                            }}
                                                        >
                                                            {d.metric.label} {d.value}
                                                            {d.metric.unit}{' '}
                                                            <span style={{ fontWeight: 500, fontSize: 11.5 }}>
                                                                (평균 {d.avg}{d.metric.unit})
                                                            </span>
                                                            {!d.judgeable && (
                                                                <span style={{ fontWeight: 700, fontSize: 10.5, color: '#93A0AE', marginLeft: 4 }}>
                                                                    참고
                                                                </span>
                                                            )}
                                                            <span style={{ fontSize: 11, color: '#C99', marginLeft: 4 }}>›</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    )}

                                    {/* 비교 데이터 — 칩을 누르면 여기 열린다 */}
                                    {openMetric?.coachId === a.coach.coachId &&
                                        (() => {
                                            const m = METRICS.find((x) => x.key === openMetric.key);
                                            if (!m) return null;
                                            // 비교 기준도 센터 평균이어야 한다.
                                            // undefined 를 넘기면 목 데이터 상수와 비교된다.
                                            const cmp = compareMetric(
                                                a.coach,
                                                m,
                                                coaches,
                                                m.key === 'lessons'
                                                    ? avgLessons
                                                    : /* 밀도 평균은 그 강사의 분모와 짝이 맞아야 한다 */
                                                      centerAvgOf(avg, m.key, a.coach)
                                            );
                                            if (!cmp) return null;
                                            return <MetricCompare cmp={cmp} onClose={() => setOpenMetric(null)} />;
                                        })()}

                                    <span style={{ fontSize: 11.5, color: '#A7B0BC', marginTop: 8, display: 'block' }}>
                                        지표를 누르면 센터 전체와 비교해 볼 수 있습니다. 「참고」는 회원 사정이 섞이거나
                                        검증되지 않은 지표라 평가 근거로 쓰지 않았습니다.
                                    </span>

                                    {a.unmeasured.length > 0 && (
                                        <span style={{ fontSize: 12, color: '#A7B0BC', marginTop: 8, display: 'block' }}>
                                            표본이 모자라 아직 판단하지 않은 지표: {a.unmeasured.map((m) => m.label).join(', ')}
                                        </span>
                                    )}
                                </div>

                                {/* 전 지표 편차 */}
                                {open && (
                                    <div style={{ borderTop: `1px solid ${BORDER}`, background: '#FCFDFE', padding: '16px 20px' }}>
                                        <span style={{ fontSize: 12.5, color: '#7C8794' }}>
                                            가운데 선이 센터 평균입니다. 오른쪽으로 뻗을수록 좋습니다.
                                        </span>
                                        <div className="flex flex-col" style={{ gap: 7, marginTop: 11 }}>
                                            {a.deviations.map((d) => (
                                                <DeviationBar key={d.metric.key} d={d} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/*
                      강사를 전부 가려 둔 상태.
                      "강사가 없습니다" 라고만 하면 원장님은 데이터가 사라진 줄 안다 —
                      바로 아래에서 되돌릴 수 있다는 것을 여기서 말한다.
                    */}
                    {visible.length === 0 && (
                        <div style={{ padding: '40px 0', textAlign: 'center' }}>
                            <span style={{ fontSize: 15.5, color: '#93A0AE' }}>
                                {hiddenCoaches.length > 0
                                    ? `강사 ${hiddenCoaches.length}명을 모두 가려 두셨습니다. 아래에서 다시 보실 수 있습니다`
                                    : '조건에 맞는 강사가 없습니다'}
                            </span>
                        </div>
                    )}
                </div>

                {/*
                  가린 강사.

                  목록에서는 빠지되 **없던 일이 되지는 않는다.** 되돌리기 토스트는 몇 초 뒤
                  사라지므로, 그것만으로는 원장님이 자기가 누구를 가렸는지 다시 볼 방법이
                  없다 (회원 현황의 「가린 회원 N명 보기」와 같은 생각).

                  ⚠️ 이 목록은 **사분면 필터를 타지 않는다.** 가리기는 강사 단위·영구라
                     "이 사분면에서만 가려졌다"는 것이 없고, 필터를 옮겨 다니며 찾게 하면
                     되돌릴 길이 사실상 막힌다. 그래서 `visible` 이 아니라 `hiddenCoaches`
                     (= allCoaches 기준) 로 그린다.
                */}
                {hiddenCoaches.length > 0 && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${BORDER}` }}>
                        <button
                            type="button"
                            onClick={() => setShowHidden((v) => !v)}
                            aria-expanded={showHidden}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 13.5,
                                color: '#93A0AE',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                            }}
                        >
                            가린 강사 {hiddenCoaches.length}명 {showHidden ? '접기' : '보기'}
                        </button>

                        {showHidden && (
                            <div
                                className="flex flex-col rounded-[11px]"
                                style={{
                                    marginTop: 11,
                                    background: '#FBFCFE',
                                    border: `1px solid ${BORDER}`,
                                    padding: '6px 0',
                                }}
                            >
                                {hiddenCoaches.map((c) => (
                                    <div
                                        key={c.coachId}
                                        className="flex items-center"
                                        style={{ padding: '9px 16px', gap: 10 }}
                                    >
                                        <span style={{ fontSize: 14, color: '#5B6B7F' }}>
                                            {c.name}
                                            {/*
                                              클로징 표에만 남은 강사(대개 퇴사자)는 담당·수업
                                              수를 모른다. 0 으로 적으면 "수업을 안 한 강사"가
                                              되므로, 아는 만큼만 적고 왜 비었는지 밝힌다.
                                            */}
                                            {c.row ? (
                                                <>
                                                    {' '}
                                                    · 담당 {c.row.members}명 · 수업 {c.row.lessons}회
                                                </>
                                            ) : (
                                                <span
                                                    style={{ color: '#A7B0BC' }}
                                                    title="이번 달 강사 목록에 없는 강사입니다 (퇴사 등). 신규기회 기록에만 남아 있어 담당·수업 수를 알 수 없습니다."
                                                >
                                                    {' '}
                                                    · 이번 달 목록에 없는 강사
                                                </span>
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleUnhide(c)}
                                            disabled={pendingHideId != null}
                                            aria-disabled={pendingHideId != null}
                                            aria-label={`${c.name} 강사 다시 보기`}
                                            style={{
                                                marginLeft: 'auto',
                                                border: 'none',
                                                background: 'transparent',
                                                color: pendingHideId != null ? '#B4BDC8' : '#2C4C8C',
                                                fontSize: 13.5,
                                                fontWeight: 700,
                                                cursor: pendingHideId != null ? 'progress' : 'pointer',
                                                textDecoration: 'underline',
                                                textUnderlineOffset: 3,
                                                padding: 0,
                                                flex: 'none',
                                            }}
                                        >
                                            다시 보기
                                        </button>
                                    </div>
                                ))}
                                {/*
                                  숨긴 것과 빼지 못한 것을 구분해서 밝힌다.
                                  센터 평균은 서버가 계산해 내려주는 값이라 가린 강사가 계속
                                  들어 있다. 안 밝히면 원장님은 평균선까지 움직였다고 믿는다.
                                */}
                                <span
                                    style={{
                                        fontSize: 12.5,
                                        color: '#93A0AE',
                                        padding: '6px 16px 4px',
                                        lineHeight: '18px',
                                    }}
                                >
                                    ※ 가린 강사는 위 목록·사분면·아래 지표 표·순위에서 빠집니다.
                                    다만 <b>센터 평균은 그대로</b>입니다 — 평균을 함께 줄이면 남은
                                    강사들의 상대 평가가 조용히 달라집니다.
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ④ 원본 표 — 숫자로 확인하고 싶을 때만 */}
            <div className="rounded-[14px]" style={{ border: `1px solid ${BORDER}`, overflow: 'hidden', background: '#FFFFFF' }}>
                <button
                    type="button"
                    onClick={() => setOpenTable((v) => !v)}
                    className="flex w-full items-center"
                    style={{ padding: '15px 20px', background: openTable ? '#F7F9FC' : '#FFFFFF', border: 'none', cursor: 'pointer', gap: 10, textAlign: 'left' }}
                >
                    <span style={{ fontSize: 15.5, fontWeight: 700, color: '#16202E' }}>전체 지표 한눈에 보기</span>
                    <span style={{ fontSize: 13, color: '#93A0AE' }}>
                        강사 {coaches.length}명 × 지표 9개 · 정렬 가능
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 13, color: '#5B6B7F', fontWeight: 600 }}>
                        {openTable ? '접기 ▲' : '펼치기 ▼'}
                    </span>
                </button>

                {openTable && (
                    <div style={{ overflowX: 'auto', borderTop: `1px solid ${BORDER}` }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
                            <thead>
                                <tr style={{ background: '#F7F9FC' }}>
                                    {COLUMNS.map((col) => (
                                        <th
                                            key={col.key}
                                            onClick={() => toggleSort(col.key)}
                                            style={{
                                                padding: '10px 12px', textAlign: col.align ?? 'left',
                                                fontSize: 12.5, fontWeight: 700,
                                                color: sortKey === col.key ? '#1A305D' : '#7C8794',
                                                cursor: 'pointer', whiteSpace: 'nowrap',
                                                borderBottom: `1px solid ${BORDER}`, userSelect: 'none',
                                            }}
                                        >
                                            {col.label}
                                            {sortKey === col.key && <span style={{ marginLeft: 4 }}>{asc ? '▲' : '▼'}</span>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c) => (
                                    <tr
                                        key={c.coachId}
                                        onClick={() => setSelected(c)}
                                        style={{ cursor: 'pointer', borderBottom: '1px solid #F1F4F9' }}
                                    >
                                        <td style={{ padding: '11px 12px' }}>
                                            <div className="flex items-center" style={{ gap: 7 }}>
                                                <span style={{ width: 7, height: 7, borderRadius: 4, background: GRADE_META[byId.get(c.coachId)!.grade].color, flex: 'none' }} />
                                                <span style={{ fontSize: 14.5, fontWeight: 700, color: '#16202E' }}>{c.name}</span>
                                            </div>
                                        </td>
                                        {COLUMNS.slice(1).map((col) => {
                                            // 회원당 수업 수는 저장된 값이 아니라 파생 값이다
                                            const v =
                                                col.key === 'lessonsPerMember'
                                                    ? lessonDensityOf(c)
                                                    : (c[col.key as keyof CoachRow] as number | null);
                                            if (col.key === 'portfolioValue')
                                                return (
                                                    <td key={col.key} style={{ padding: '11px 12px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: '#16202E', whiteSpace: 'nowrap' }}>
                                                        {won(c.portfolioValue)}원
                                                    </td>
                                                );
                                            if (v == null) {
                                                /* 왜 비었는지를 서버 분모로 설명한다 — 「표본부족」은 근거 없는 말이었다 */
                                                const reason = emptyCellReason(col.key, c.basis);
                                                return (
                                                    <td
                                                        key={col.key}
                                                        title={reason.title}
                                                        style={{ padding: '11px 12px', textAlign: 'right', fontSize: 13, color: '#B4BDC9', whiteSpace: 'nowrap', cursor: 'help' }}
                                                    >
                                                        {reason.text}
                                                    </td>
                                                );
                                            }
                                            let color = '#3A4757';
                                            if (col.avg != null) {
                                                const better = col.lowerIsBetter ? v <= col.avg : v >= col.avg;
                                                const bad = col.lowerIsBetter ? v > col.avg * 1.5 : v < col.avg * 0.7;
                                                color = bad ? '#C22D38' : better ? '#00795F' : '#3A4757';
                                            }
                                            return (
                                                <td key={col.key} style={{ padding: '11px 12px', textAlign: 'right', fontSize: 14, fontWeight: col.avg != null ? 700 : 500, color, whiteSpace: 'nowrap' }}>
                                                    {v}
                                                    <span style={{ fontSize: 11, color: '#A7B0BC' }}>{col.unit}</span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: '#F7F9FC' }}>
                                    {/*
                                      ⚠️ 이 평균은 **서버가 계산해 내려준 값**(centerAvg)이라
                                         가린 강사가 계속 들어 있다. 서버가 분모를 몰래 줄이면
                                         남은 강사들의 상대 평가가 조용히 달라지기 때문이다.
                                         반면 위의 줄들은 가린 강사를 뺀 목록이다.
                                         두 숫자의 기준이 다르므로 화면이 스스로 밝힌다.
                                    */}
                                    <td style={{ padding: '11px 12px', fontSize: 13, fontWeight: 700, color: '#5B6B7F' }}>
                                        센터 평균
                                        {hiddenCoaches.length > 0 && (
                                            <span
                                                title="센터 평균은 서버가 센터 전체로 계산한 값이라 가린 강사도 포함합니다. 위 줄 목록에서만 뺐습니다."
                                                style={{ fontSize: 11, fontWeight: 600, color: '#A7B0BC', cursor: 'help' }}
                                            >
                                                {' '}(가린 강사 포함)
                                            </span>
                                        )}
                                    </td>
                                    {/*
                                      평균 행을 손으로 나열해 두면 컬럼이 하나 빠질 때마다
                                      숫자가 통째로 한 칸씩 밀립니다. 컬럼 정의에서 그대로 뽑습니다.
                                    */}
                                    {COLUMNS.slice(1).map((col) =>
                                        col.key === 'portfolioValue' ? (
                                            <td key={col.key} style={{ padding: '11px 12px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#5B6B7F' }}>
                                                {won(totalPortfolio)}원
                                            </td>
                                        ) : (
                                            <td key={col.key} style={{ padding: '11px 12px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: col.avg == null ? '#7C8794' : '#5B6B7F' }}>
                                                {/* 나눗셈에서 온 평균은 자릿수가 길다. 표는 특히 눈에 띈다 */}
                                                {col.avg == null ? '—' : round1(col.avg)}
                                            </td>
                                        )
                                    )}
                                </tr>
                            </tfoot>
                        </table>
                        {droppedColumns.length > 0 && (
                            <div style={{ padding: '10px 12px', borderTop: '1px solid #F1F4F9' }}>
                                <span style={{ fontSize: 12, color: '#93A0AE' }}>
                                    아직 집계하지 않는 지표는 표에서 뺐습니다: {droppedColumns.join(' · ')} —
                                    라포에 기록이 쌓이면 자동으로 다시 나타납니다.
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/*
              강사를 누르면 곧바로 완성본 리포트가 열린다.
              예전에는 여기만 우측 슬라이드 패널(CoachDetailDrawer)이라
              상단 '강사 성과 리포트' 버튼과 **다른 화면**이 나왔다.
            */}
            {/*
              강사 데이터 드로어 — 오늘의 브리핑 강사 카드가 여는 것과 **같은 컴포넌트**다.
              `analyses` 를 그대로 넘긴다(이 화면이 이미 센터 평균으로 계산해 둔 것).
              드로어가 다시 계산하면 같은 강사가 화면마다 다른 등급을 받는다.
            */}
            {(() => {
                const a = openData ? byId.get(openData) : undefined;
                if (!a) return null;
                return (
                    <CoachDataDrawer
                        analysis={a}
                        /* 순위 모집단은 **가린 강사를 뺀 목록** — 위 표·사분면과 같은 기준 */
                        peers={coaches}
                        month={date.format('YYYY-MM')}
                        convMid={convMid}
                        careMid={careMid}
                        onClose={() => setOpenData(null)}
                        onOpenReport={() => setSelected(a.coach)}
                    />
                );
            })()}

            {selected && (
                <CoachReportSheet
                    coach={selected}
                    month={date.format('YYYY-MM')}
                    onClose={() => setSelected(null)}
                />
            )}

            {/*
              왜 이 금액인가 — 「강사들이 들고 있는 잔여 수강권」 (2026-09-01 QA31).

              🔴 합계는 헤더가 그린 `totalPortfolio` 그대로이고, 줄은 그 합계를 만든
                 바로 그 목록(`coaches` — 가린 강사를 뺀 목록)이다.

              ⚠️ 강사 줄에는 「잔여 N회 × 단가」를 **못 적는다.** `portfolioValue` 는
                 서버가 회원별 선수금을 이미 합쳐 보내 주는 값이라, 여기에는 회원
                 단위 원본이 없다. 회원별 내역은 회원 현황 헤더가 편다 —
                 없는 근거를 지어내지 않는다.
            */}
            {showPortfolio && (
                <ValueBreakdownModal
                    title="강사들이 들고 있는 잔여 수강권"
                    definition={
                        '강사가 담당한 회원들의 미사용 선수금 합계입니다 — 잔여 회차 × 회당 단가. ' +
                        '이미 받았지만 아직 수업하지 않은 금액이라, 그 강사가 그만두거나 회원이 떠나면 실제로 흔들리는 돈입니다.'
                    }
                    scopeNote={`${date.format('YYYY년 M월')} · 강사 ${coaches.length}명 기준`}
                    rows={coaches.map((c) => ({
                        key: c.coachId,
                        name: c.name,
                        sub: `담당 ${c.members}명`,
                        amount: c.portfolioValue,
                    }))}
                    total={totalPortfolio}
                    policyNotes={[
                        '회원별 내역은 회원 현황의 「지켜야 할 매출」에서 볼 수 있습니다.',
                        '재등록 기대는 더하지 않습니다. 이미 받은 돈과 앞으로 받을 기댓값은 성격이 다릅니다.',
                        ...(hiddenCoaches.length > 0
                            ? [`가려 둔 강사 ${hiddenCoaches.length}명은 이 합계에서 빠져 있습니다.`]
                            : []),
                    ]}
                    onClose={() => setShowPortfolio(false)}
                />
            )}

            {toast && (
                <div
                    className="fixed left-1/2 flex items-center rounded-[10px]"
                    style={{
                        bottom: 40,
                        transform: 'translateX(-50%)',
                        background: '#1A305D',
                        color: '#FFFFFF',
                        fontSize: 15.5,
                        padding: '14px 22px',
                        gap: 16,
                        zIndex: 1000,
                        boxShadow: '0 8px 24px rgba(26,48,93,0.24)',
                    }}
                >
                    <span>{toast.text}</span>
                    {/*
                      되돌릴 수 있는 동작은 **그 자리에서** 되돌린다.
                      되돌릴 길을 목록 아래로만 두면, 잘못 눌렀다는 것을 알아챈
                      순간 어디를 눌러야 하는지 모른다 (회원 현황과 같은 규칙).
                    */}
                    {toast.undo && (
                        <button
                            type="button"
                            onClick={() => {
                                const run = toast.undo?.run;
                                setToast(null);
                                run?.();
                            }}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#9DC2FF',
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                padding: 0,
                                flex: 'none',
                            }}
                        >
                            {toast.undo.label}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default OpsBriefingCoachesPage;
